// src/features/wbs/WbsListContainer.tsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toProjectDateOnly, toISODate, activeWeekAnchorOf, dowLabelKoShort } from '../../utils/date';
// import { saveWbs } from '../../lib/wbs/wbsStore';
import { listFlags } from '../../lib/wbs/progressService';
import type { WeekFlag, WbsFlatItem } from './types';
import { buildRollupRows } from './lib/progressRollup';
// import Overview, { type FilterPreset } from './components/overview/Overview';
import Overview, { type FilterPreset } from './components/overview-v2/OverviewV2';
import ApprovalQueueModal from './components/overview/ApprovalQueueModal';
import { WbsList } from './WbsListV2';
import { apiListTasks } from 'src/features/tasks/api/tasks.api';
import { apiListRisks } from 'src/features/risks/api/risks.api';
import WbsLinksModal from './components/WbsLinksModal';
import type { WbsItemVersion } from './types';
import {
    listVersions,
    getListByVersion,
    apiGetApprovedMap,
    apiGetWeeklyProgressComparison,
    apiBackfillProgressHistory,
    apiResetBackfillHistory,
} from './api';
import { STATUS_LABEL_KO, type WbsStatusKind } from './components/common/wbsStatus';
import { WbsFilterBar } from './components/WbsFilterBar';
import type { WbsFilters, WbsFilterOptions, WeeklyFilterKey } from './components/WbsFilterBar';
import { statusFromRow } from './components/common/statusFromRow';
import WbsOpsToolbar from './components/WbsOpsToolbar';

import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';
// import { useProjectSetting } from 'src/features/projects/hooks/useProjectSetting';

import type { WorkPackage, Company } from './components/overview/CompanyExecutionSummary';

import { useWbsCurrent } from './repo';
import { WbsViewAs, type WbsEvViewMode } from './components/overview/AsOfSelector';

const SOON_DAYS = 7;

function isWithinDays(iso: string | null | undefined, base: Date, days: number) {
    if (!iso) return false;
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return false;
    const diffDays = (d.getTime() - base.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= days;
}

// 트리 인덱스(필터용)
function buildTreeIndexForFilter(rows: WbsFlatItem[]) {
    const parentMap: Record<string, string | null> = {};
    const childrenMap: Record<string, string[]> = {};
    for (const r of rows) {
        parentMap[r.id] = r.parentId ?? null;
        if (r.parentId) {
            if (!childrenMap[r.parentId]) childrenMap[r.parentId] = [];
            childrenMap[r.parentId].push(r.id);
        }
        if (!childrenMap[r.id]) childrenMap[r.id] = childrenMap[r.id] ?? [];
    }
    return { parentMap, childrenMap };
}

function filterKeepHierarchy(
    rows: WbsFlatItem[],
    idx: ReturnType<typeof buildTreeIndexForFilter>,
    predicate: (r: WbsFlatItem) => boolean,
) {
    const keep = new Set<string>();

    // match
    for (const r of rows) {
        if (predicate(r)) keep.add(r.id);
    }

    // ancestors
    for (const id of Array.from(keep)) {
        let p = idx.parentMap[id];
        while (p) {
            keep.add(p);
            p = idx.parentMap[p];
        }
    }

    // orderIndex 순서 유지
    return rows.filter((r) => keep.has(r.id));
}

export default function WbsListContainer({
    projectId,
    canEdit = false,
    canApprove = false,
    canAssign = false,
    // mode = 'viewer',
    // actorProjectCompanyId,
    // compact = false,
}: {
    projectId: string;
    canEdit?: boolean;
    canApprove?: boolean;
    canAssign?: boolean;
    // mode: WbsListMode;
    // actorProjectCompanyId?: string | null;
    // compact?: boolean;
}) {
    // 베이스 날짜
    const { setting, isLoading: settingLoading } = useProjectSettingCtx();
    const [viewAs, setViewAs] = useState<WbsViewAs>('WEEK');
    const [evViewMode, setEvViewMode] = useState<WbsEvViewMode>('LIVE');
    const [baseDate, setBaseDate] = useState<Date>(new Date());
    const [weekOfISO, setWeekOfISO] = useState<string | undefined>();

    const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);

    // const { data: psRes } = useProjectSetting(projectId);
    // const projectTz = psRes?.data?.timeZone ?? 'Asia/Seoul';
    const projectTz = setting?.timeZone ?? 'Asia/Seoul';

    const effectiveAnchorDow = useMemo(() => {
        // 기존 프론트 기본값(금요일=5)유지. 나중에 지울 것
        const current = setting?.wbsWeekAnchorDow ?? 5;
        const pending = setting?.wbsWeekAnchorDowPending ?? null;
        const from = setting?.wbsWeekAnchorEffectiveFrom ? new Date(setting.wbsWeekAnchorEffectiveFrom) : null;

        // backend/src/modules/projects/settings/schema.ts의 판단 로직과 동일
        if (pending != null && from && baseDate >= from) return pending;

        return current;
    }, [baseDate, setting]);

    const liveAnchorDate = useMemo(() => {
        if (viewAs !== 'WEEK') {
            const d = new Date(baseDate);
            d.setHours(0, 0, 0, 0);
            return d;
        }
        return activeWeekAnchorOf(baseDate, effectiveAnchorDow);
    }, [baseDate, effectiveAnchorDow, viewAs]);

    const confirmedW1AnchorDate = useMemo(() => {
        const d = new Date(liveAnchorDate);
        d.setDate(d.getDate() - 7);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [liveAnchorDate]);

    const confirmedW2AnchorDate = useMemo(() => {
        const d = new Date(liveAnchorDate);
        d.setDate(d.getDate() - 14);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [liveAnchorDate]);

    // [DB] 현재 리스트/항목 로딩
    // const { data, isLoading, error, refetch } = useWbsCurrent(projectId);
    // const { data, isLoading, error, refetch } = useWbsCurrent(projectId, { viewAs, at: baseDate });
    const wbsFetchAt = useMemo(() => {
        if (viewAs !== 'WEEK') return baseDate;
        if (evViewMode === 'LIVE') return liveAnchorDate;
        if (evViewMode === 'CONFIRMED_W1') return confirmedW1AnchorDate;
        return confirmedW2AnchorDate;
    }, [baseDate, viewAs, evViewMode, liveAnchorDate, confirmedW1AnchorDate, confirmedW2AnchorDate]);

    const { data, isLoading, error, refetch } = useWbsCurrent(projectId, { viewAs, at: wbsFetchAt });
    const currentVersion = data?.list?.version;

    const refFriday = useMemo(() => {
        if (viewAs === 'WEEK') {
            return liveAnchorDate;
        }
        // DATE 모드에서는 기존의 today의 금요일을 쓰지 말고 baseDate(00:00)로 맞추는 게 일관됨
        const d = new Date(baseDate);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [baseDate, liveAnchorDate, viewAs]);

    // const effectiveWeekOfISO = useMemo(() => toISODate(refFriday), [refFriday]);
    const liveWeekOfISO = useMemo(() => toISODate(liveAnchorDate), [liveAnchorDate]);
    const confirmedW1WeekOfISO = useMemo(() => toISODate(confirmedW1AnchorDate), [confirmedW1AnchorDate]);
    const confirmedW2WeekOfISO = useMemo(() => toISODate(confirmedW2AnchorDate), [confirmedW2AnchorDate]);

    const effectiveWeekOfISO = useMemo(() => {
        if (evViewMode === 'LIVE') return liveWeekOfISO;
        if (evViewMode === 'CONFIRMED_W1') return confirmedW1WeekOfISO;
        return confirmedW2WeekOfISO;
    }, [evViewMode, liveWeekOfISO, confirmedW1WeekOfISO, confirmedW2WeekOfISO]);

    const currentWeekOfISO = useMemo(() => {
        return toISODate(activeWeekAnchorOf(new Date(), effectiveAnchorDow));
    }, [effectiveAnchorDow]);

    const liveAvailable = useMemo(() => {
        return selectedVersion === currentVersion && liveWeekOfISO === currentWeekOfISO;
    }, [selectedVersion, currentVersion, liveWeekOfISO, currentWeekOfISO]);

    useEffect(() => {
        if (selectedVersion == null || currentVersion == null) return;

        if (!liveAvailable && evViewMode === 'LIVE') {
            setEvViewMode('CONFIRMED_W1');
        }
    }, [selectedVersion, currentVersion, liveAvailable, evViewMode]);

    useEffect(() => {
        if (viewAs === 'WEEK') {
            setWeekOfISO(effectiveWeekOfISO);
        } else {
            setWeekOfISO(undefined);
        }
    }, [effectiveWeekOfISO, viewAs]);

    const anchorDowLabel = useMemo(() => {
        return viewAs === 'WEEK' ? dowLabelKoShort(effectiveAnchorDow) : undefined;
    }, [viewAs, effectiveAnchorDow]);

    // [STR-ID] rows/flags/counts 모두 string 키 사용
    const [rows, setRows] = useState<WbsFlatItem[]>([]);
    const [flags, setFlags] = useState<Record<string, WeekFlag>>({});
    const [approvalOpen, setApprovalOpen] = useState(false);
    const [approvedMap, setApprovedMap] = useState<Record<string, { approvedValue: number; sourceWeekOf: string }>>({});
    const [approvedMapW1, setApprovedMapW1] = useState<Record<string, { approvedValue: number; sourceWeekOf: string }>>(
        {},
    );
    const [approvedMapW2, setApprovedMapW2] = useState<Record<string, { approvedValue: number; sourceWeekOf: string }>>(
        {},
    );
    const [weeklyProgressComparison, setWeeklyProgressComparison] = useState<any | null>(null);
    const [counts, setCounts] = useState<Record<string, { tasks: number; risks: number }>>({});
    const [openLinks, setOpenLinks] = useState(false);
    const [linksTarget, setLinksTarget] = useState<{ id: string; nodeId: string; code: string; name: string } | null>(
        null,
    );
    const [linksMode, setLinksMode] = useState<'risk' | 'task'>('risk');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
    const [versions, setVersions] = useState<
        Array<{ version: number; isCurrent: boolean; createdAt?: string; activatedAt?: string }>
    >([]);

    // ===== Filters (계산은 Container에서) =====
    const [filters, setFilters] = useState<WbsFilters>(() => ({
        companyId: '',
        // companyId: compact && actorProjectCompanyId ? actorProjectCompanyId : '',
        statusKinds: [],
        weeklyKinds: [],
        startSoon: false,
        finishSoon: false,
        unassignedOnly: false,
        leafOnly: false,
        hasRiskOnly: false,
        hasTaskOnly: false,
    }));

    // useEffect(() => {
    //     if (!compact) return;
    //     if (!actorProjectCompanyId) return;
    //     setFilters((prev) => ({ ...prev, companyId: actorProjectCompanyId }));
    // }, [compact, actorProjectCompanyId]);

    useEffect(() => {
        listVersions(projectId)
            .then(setVersions)
            .catch(() => setVersions([]));
    }, [projectId]);

    useEffect(() => {
        // if (currentVersion !== null) {
        if (currentVersion != null) {
            // null / undefined 둘 다 막음
            setSelectedVersion(currentVersion);
        }
    }, [currentVersion]);

    const onOpenLinks = useCallback(
        (mode: 'risk' | 'task', item: { id: string; nodeId: string; code: string; name: string }) => {
            setLinksMode(mode);
            setLinksTarget(item);
            setOpenLinks(true);
        },
        [],
    );

    // counts 재계산
    const refreshCounts = useCallback(async () => {
        // if (compact) {
        //     setCounts({});
        //     return;
        // }

        if (!rows.length) {
            setCounts({});
            return;
        }

        const tasks = await apiListTasks(projectId, { includeInactive: true });
        const risks = await apiListRisks(projectId, { includeClosed: true });

        const map: Record<string, { tasks: number; risks: number }> = {};
        for (const r of rows) {
            const id = r.id;

            map[id] = {
                tasks: tasks.filter((t: any) => String(t.wbsNodeId) === String(r.nodeId)).length,
                risks: risks.filter((k: any) => String(k.wbsNodeId) === String(r.nodeId)).length,
            };
        }
        setCounts(map);
    }, [projectId, rows]);

    useEffect(() => {
        refreshCounts();
    }, [refreshCounts]);

    // PV 계산 기준일
    const [pvRefDate, setPvRefDate] = useState<Date>(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });

    // 공용 매핑 함수: items => rows (id=versionId, nodeId 유지, parentId=부모 versionId)
    const mapItemsToRows = useCallback(
        (items: WbsItemVersion[]): WbsFlatItem[] => {
            if (!items?.length) return [];
            const nodeIdTovVersionId = new Map(items.map((i) => [i.nodeId, i.id]));
            return items.map((v) => {
                const parentVersionId = v.parentNodeId ? (nodeIdTovVersionId.get(v.parentNodeId) ?? null) : null;
                // const plannedStart = isoToYMD(v.plannedStart);
                // const plannedFinish = isoToYMD(v.plannedFinish);
                const plannedStart = toProjectDateOnly(v.plannedStart, projectTz);
                const plannedFinish = toProjectDateOnly(v.plannedFinish, projectTz);
                // const actualWeight = v.weight != null ? Number((v.weight * 100).toFixed(2)) : undefined;
                const actualWeight = v.weight != null ? Number(v.weight) : undefined;

                return {
                    id: v.id,
                    nodeId: v.nodeId,
                    parentId: parentVersionId,
                    level: v.level ?? 1,
                    code: v.code ?? '',
                    name: v.name ?? '',
                    actualWeight,
                    plannedStart,
                    plannedFinish,
                    duration: v.durationDays ?? undefined,
                    earnedValue: v.actualProgress != null ? Number(v.actualProgress.toFixed(1)) : null,
                    plannedValue: v.plannedProgress != null ? Number(v.plannedProgress.toFixed(1)) : null,
                    memo: undefined,
                    // executer: (undefined),
                    // executer: (v as any).responsibleCompanyName ?? null,
                    // executerId: (v as any).responsibleProjectCompanyId ?? null,
                    executer: (v as any).responsibleName ?? (v as any).responsibleCompanyName ?? null,
                    executerId: (v as any).responsibleId ?? (v as any).responsibleProjectCompanyId ?? null,
                };
            });
        },
        [projectTz],
    );

    // 선택된 버전에 따라 rows 갱산
    useEffect(() => {
        (async () => {
            if (selectedVersion == null) return;
            if (!Number.isInteger(selectedVersion) || selectedVersion <= 0) return;

            if (selectedVersion === currentVersion) {
                setRows(mapItemsToRows((data?.items ?? []) as WbsItemVersion[]));
            } else {
                const res = await getListByVersion(projectId, selectedVersion, {
                    viewAs,
                    at: baseDate.toISOString(),
                });
                setRows(mapItemsToRows((res?.items ?? []) as WbsItemVersion[]));
            }
        })();
    }, [currentVersion, data?.items, mapItemsToRows, projectId, selectedVersion, viewAs, baseDate]);

    // 주간 상태칩 플래그
    const refreshFlags = useCallback(
        async (rowsIn: WbsFlatItem[]) => {
            // project setting 로딩/없으면 서버 호출하지 않음
            if (settingLoading || !setting) return;

            if (!rowsIn?.length) {
                setFlags({});
                return;
            }

            const nodeIds = rowsIn.map((r) => r.nodeId);
            const resByNode: Record<string, WeekFlag> = await listFlags(projectId, effectiveWeekOfISO, nodeIds);

            // UI는 versionId 키를 사용 -> nodeId -> versionId로 변환
            const nodeIdToVid = new Map(rowsIn.map((r) => [r.nodeId, r.id]));
            const byVersion: Record<string, WeekFlag> = {};
            for (const [nodeId, flag] of Object.entries(resByNode)) {
                const vid = nodeIdToVid.get(nodeId);
                if (vid) byVersion[vid] = flag;
            }
            setFlags(byVersion);
        },
        [projectId, effectiveWeekOfISO, setting, settingLoading],
    );

    useEffect(() => {
        if (!rows.length) {
            setFlags({});
            return;
        }
        refreshFlags(rows);
    }, [rows, refreshFlags]);

    useEffect(() => {
        if (settingLoading || !setting) return;
        setApprovedMap({});
        setApprovedMapW1({});
        setApprovedMapW2({});

        if (!rows.length) {
            return;
        }
        const nodeIds = rows.map((r) => r.nodeId);
        Promise.all([
            apiGetApprovedMap(projectId, effectiveWeekOfISO, nodeIds).catch(() => ({})),
            apiGetApprovedMap(projectId, confirmedW1WeekOfISO, nodeIds).catch(() => ({})),
            apiGetApprovedMap(projectId, confirmedW2WeekOfISO, nodeIds).catch(() => ({})),
        ]).then(([currentMap, w1Map, w2Map]) => {
            setApprovedMap(currentMap);
            setApprovedMapW1(w1Map);
            setApprovedMapW2(w2Map);
        });
    }, [projectId, effectiveWeekOfISO, confirmedW1WeekOfISO, confirmedW2WeekOfISO, rows, setting, settingLoading]);

    useEffect(() => {
        // if (compact) {
        //     setWeeklyProgressComparison(null);
        //     return;
        // }

        if (settingLoading || !setting) return;
        if (!rows.length) return;

        setWeeklyProgressComparison(null);

        const nodeIds = rows.map((r) => r.nodeId);
        const atISO = new Date(baseDate).toISOString();

        apiGetWeeklyProgressComparison(projectId, atISO, nodeIds)
            .then(setWeeklyProgressComparison)
            .catch(() => setWeeklyProgressComparison(null));
    }, [projectId, rows, baseDate, settingLoading, setting]);

    // 메타 저장(로컬) — 기존 동작 유지
    const handleSaveRows = useCallback((next: WbsFlatItem[]) => {
        // saveWbs(projectId, next); // [STR-ID] projectId string
        setRows(next);
    }, []);

    const onFilterPreset = useCallback((preset: FilterPreset) => {
        console.log('apply preset filter:', preset);
    }, []);

    // const baseRows = useMemo(() => rows, [rows]);
    const resolveLiveEarnedValue = useCallback(
        (row: WbsFlatItem): number | null => {
            const confirmed = row.earnedValue ?? null;
            const fallbackApproved = approvedMap[row.nodeId]?.approvedValue ?? confirmed ?? null;
            const previousWeekApproved = approvedMapW1[row.nodeId]?.approvedValue ?? fallbackApproved;
            const f = flags[row.id];
            const previousWeekEffective =
                f?.previousWeekStatus === 'PENDING' || f?.previousWeekStatus === 'APPROVED'
                    ? (f.previousWeekDraftEV ?? previousWeekApproved)
                    : previousWeekApproved;

            if (!liveAvailable || evViewMode !== 'LIVE') {
                return confirmed;
            }

            if (!f) return previousWeekApproved;

            if (f.statusForWeek === 'APPROVED') {
                return confirmed;
            }

            if (f.statusForWeek === 'PENDING') {
                return f.draftEV != null ? f.draftEV : previousWeekEffective;
            }

            if (f.statusForWeek === 'REJECTED') {
                return previousWeekEffective;
            }

            if (f.draftEV != null) {
                return f.draftEV;
            }

            return previousWeekEffective;
        },
        [approvedMap, approvedMapW1, evViewMode, flags, liveAvailable],
    );

    const baseRows = useMemo(() => {
        return rows.map((r) => ({
            ...r,
            // earnedValue: resolveLiveEarnedValue(r),
            liveEarnedValue: resolveLiveEarnedValue(r),
        }));
    }, [rows, resolveLiveEarnedValue]);

    const rollupRows = useMemo(() => {
        return buildRollupRows(baseRows);
    }, [baseRows]);

    const filterOptions: WbsFilterOptions = useMemo(() => {
        const companiesMap = new Map<string, string>();
        const statusSet = new Set<string>();

        for (const r of rollupRows) {
            if (r.executerId) companiesMap.set(r.executerId, r.executer ?? r.executerId);

            // const pv = plannedPercentAt(r, pvRefDate);
            const pv = r.plannedValue ?? null;
            const kind = String(statusFromRow(r, pv, pvRefDate));
            statusSet.add(kind);
        }

        return {
            companies: Array.from(companiesMap.entries()).map(([id, name]) => ({ id, name })),
            statusKinds: Array.from(statusSet.values()).sort(),
        };
    }, [rollupRows, pvRefDate]);

    const companySummaryWorkPackages: WorkPackage[] = useMemo(() => {
        const parentSet = new Set<string>();

        for (const r of rollupRows) {
            if (r.parentId) parentSet.add(String(r.parentId));
        }

        return rollupRows
            .filter((r) => !parentSet.has(String(r.id))) // leaf only
            .map((r) => ({
                id: r.id,
                weight: Number((r as any).actualWeight ?? r.weight ?? 0),
                ev: Number((r as any).earnedValue ?? 0),
                pv: Number(r.plannedValue ?? 0),
                executorCompanyId: (r as any).executerId ?? null,
            }));
    }, [rollupRows]);

    const companySummaryCompanies: Company[] = useMemo(() => {
        return filterOptions.companies.map((c) => ({
            id: c.id,
            name: c.name,
        }));
    }, [filterOptions.companies]);

    const handleSelectCompanyFromSummary = useCallback((companyId: string | null) => {
        setFilters((prev) => ({
            ...prev,
            companyId: prev.companyId === (companyId ?? '') ? '' : (companyId ?? ''),
        }));
    }, []);

    const filterSummary = useMemo(() => {
        const chips: string[] = [];
        if (filters.companyId) {
            const name = filterOptions.companies.find((c) => c.id === filters.companyId)?.name ?? filters.companyId;
            chips.push(`회사: ${name}`);
        }
        if (filters.unassignedOnly) chips.push('미배정');
        // if (filters.statusKinds.length) chips.push(`상태: ${filters.statusKinds.join(',')}`);
        if (filters.statusKinds.length) {
            chips.push(`상태: ${filters.statusKinds.map((k) => STATUS_LABEL_KO[k as WbsStatusKind] ?? k).join(',')}`);
        }
        if (filters.weeklyKinds.length) chips.push(`주간: ${filters.weeklyKinds.join(',')}`);
        if (filters.startSoon) chips.push('착수임박(7일)');
        if (filters.finishSoon) chips.push('완료임박(7일)');
        if (filters.leafOnly) chips.push('WP(leaf)만');
        if (filters.hasRiskOnly) chips.push('Risk 있음');
        if (filters.hasTaskOnly) chips.push('Task 있음');
        return chips;
    }, [filters, filterOptions]);

    // ===== filtered list rows (계층 유지) =====
    // 3) 필터(회사/상태/주간/임박/leaf/risk/task) + 계층 유지(ancestor 포함)까지 적용한 최종 rows
    // - WbsList에는 반드시 이 filteredRows를 전달해야 필터가 실제 화면에 반영됨
    const filteredRows = useMemo(() => {
        // 트리 인덱스는 "현재 화면 기준 rows" (승인 EV 반영된 rows)로 생성해야 일관됨
        const idx = buildTreeIndexForFilter(rollupRows);

        const hasCompany = !!filters.companyId;
        const hasStatus = filters.statusKinds.length > 0;
        const hasWeekly = filters.weeklyKinds.length > 0;
        const now = new Date();

        const weeklySet = new Set(filters.weeklyKinds);
        const statusSet = new Set(filters.statusKinds);

        return filterKeepHierarchy(rollupRows, idx, (r) => {
            // 회사
            if (filters.unassignedOnly && !!r.executerId) return false;
            if (hasCompany && r.executerId !== filters.companyId) return false;

            // 상태(kind)
            if (hasStatus) {
                // const pv = plannedPercentAt(r, pvRefDate);
                const pv = r.plannedValue ?? null;
                const kind = String(statusFromRow(r, pv, pvRefDate));
                if (!statusSet.has(kind)) return false;
            }

            // 주간 상태
            if (hasWeekly) {
                const f = (flags ?? {})[r.id];
                const status = (f?.statusForWeek ?? null) as any;
                const hasDraft = f?.draftEV != null;

                let wk: WeeklyFilterKey;
                if (!hasDraft && !status) wk = 'NO_INPUT';
                else if (status === 'APPROVED') wk = 'APPROVED';
                else if (status === 'PENDING') wk = 'PENDING';
                else if (status === 'REJECTED') wk = 'REJECTED';
                else wk = 'DRAFT';

                if (!weeklySet.has(wk)) return false;
            }

            // 임박
            if (filters.startSoon && !isWithinDays(r.plannedStart ?? null, now, SOON_DAYS)) return false;
            if (filters.finishSoon && !isWithinDays(r.plannedFinish ?? null, now, SOON_DAYS)) return false;

            // 확장: leafOnly / risk/task
            if (filters.leafOnly) {
                const children = idx.childrenMap[r.id] ?? [];
                if (children.length > 0) return false;
            }
            if (filters.hasRiskOnly) {
                const c = counts?.[r.id]?.risks ?? 0;
                if (c <= 0) return false;
            }
            if (filters.hasTaskOnly) {
                const c = counts?.[r.id]?.tasks ?? 0;
                if (c <= 0) return false;
            }

            return true;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rollupRows, filters, flags, pvRefDate, counts]);

    useEffect(() => {
        // setting이 준비되기 전이면 호출하지 않음(기존 흐름 유지)
        if (settingLoading || !setting) return;
        if (!filteredRows.length) return;

        setWeeklyProgressComparison(null);

        // ⚠️ 기존 코드 의도 유지: "현재 화면에 표시되는 rows" 기준으로 비교 데이터를 조회
        const nodeIds = filteredRows.map((r) => r.nodeId);
        const atISO = new Date(baseDate).toISOString();

        apiGetWeeklyProgressComparison(projectId, atISO, nodeIds)
            .then(setWeeklyProgressComparison)
            .catch(() => setWeeklyProgressComparison(null));
    }, [projectId, filteredRows, baseDate, settingLoading, setting]);

    const refreshAll = useCallback(async () => {
        if (isRefreshing) return;

        // project setting 로딩/없으면 서버 호출하지 않음
        if (settingLoading || !setting) return;

        setIsRefreshing(true);
        try {
            // 1) Current List + items 자체를 재조회
            await refetch?.();
            // 2) 버전 목록도 재조회
            try {
                const v = await listVersions(projectId);
                setVersions(v);
            } catch {
                setVersions([]);
            }

            await refreshFlags(rows);

            if (rows.length) {
                const nodeIds = rows.map((r) => r.nodeId);
                const [currentMap, w1Map, w2Map] = await Promise.all([
                    apiGetApprovedMap(projectId, effectiveWeekOfISO, nodeIds).catch(() => ({})),
                    apiGetApprovedMap(projectId, confirmedW1WeekOfISO, nodeIds).catch(() => ({})),
                    apiGetApprovedMap(projectId, confirmedW2WeekOfISO, nodeIds).catch(() => ({})),
                ]);
                setApprovedMap(currentMap);
                setApprovedMapW1(w1Map);
                setApprovedMapW2(w2Map);
            }
            setLastRefreshedAt(new Date());
        } finally {
            setIsRefreshing(false);
        }
    }, [
        isRefreshing,
        projectId,
        effectiveWeekOfISO,
        confirmedW1WeekOfISO,
        confirmedW2WeekOfISO,
        refetch,
        refreshFlags,
        rows,
        setting,
        settingLoading,
    ]);

    const runBackfillHistory = useCallback(
        async (x: { targetEvToPvRatio: number; pattern: 'UNIFORM' | 'FRONT_DELAY' | 'MID_DIP' }) => {
            await apiBackfillProgressHistory(projectId, {
                targetEvToPvRatio: x.targetEvToPvRatio,
                // weeksBack: x.weeksBack,
                pattern: x.pattern,
            });

            // ✅ 백필 후 즉시 화면 반영
            await refreshAll();
        },
        [projectId, refreshAll],
    );

    const runResetBackfillHistory = useCallback(async () => {
        if (!window.confirm('AUTO_BACKFILL 데이터를 모두 삭제하시겠습니까?')) return;

        await apiResetBackfillHistory(projectId);
        await refreshAll();
    }, [projectId, refreshAll]);

    const entryWindow = useMemo(() => {
        const closeAt = new Date(refFriday);

        closeAt.setDate(closeAt.getDate() + 1);
        closeAt.setHours(0, 0, 0, 0);

        const auto = new Date(closeAt);
        auto.setDate(auto.getDate() + 7);
        auto.setHours(0, 0, 0, 0);

        return { closeAt, autoApproveAt: auto };
    }, [refFriday, setting]);

    const entryClosed = useMemo(() => new Date() >= entryWindow.closeAt, [entryWindow]);
    const approveEnabled = !!setting?.wbsEvApproveBeforeClose || entryClosed;

    if (settingLoading) return <div className="mt-2 text-xs text-gray-500">Loading Project Setting…</div>;
    if (!setting) return <div className="mt-2 text-xs text-red-600">Project Setting missing</div>;

    if (isLoading) return <div className="mt-2 text-xs text-gray-500">Loading WBS…</div>;
    if (error) return <div className="mt-2 text-xs text-red-600">WBS 로딩 실패</div>;

    return (
        <div>
            <WbsOpsToolbar
                canEdit={canEdit}
                onRunBackfillHistory={runBackfillHistory}
                onResetBackfillHistory={runResetBackfillHistory}
            />
            <Overview
                rows={baseRows}
                flags={flags}
                onOpenApprovalQueue={() => {
                    if (!canApprove) return;
                    setApprovalOpen(true);
                }}
                // onRunBackfillHistory={runBackfillHistory}
                // onResetBackfillHistory={runResetBackfillHistory}
                onFilterPreset={onFilterPreset}
                onRefresh={refreshAll}
                refreshing={isRefreshing}
                lastRefreshedAt={lastRefreshedAt}
                viewAs={viewAs}
                baseDate={baseDate}
                onChangeViewAs={setViewAs}
                onChangeBaseDate={setBaseDate}
                weekOfISO={weekOfISO}
                anchorDowLabel={anchorDowLabel}
                weeklyProgressComparison={weeklyProgressComparison}
                canEdit={canEdit}
                canApprove={canApprove}
                companySummaryWorkPackages={companySummaryWorkPackages}
                companySummaryCompanies={companySummaryCompanies}
                onSelectCompanyFromSummary={handleSelectCompanyFromSummary}
                evViewMode={evViewMode}
                onChangeEvViewMode={setEvViewMode}
                liveAvailable={liveAvailable}
            />
            {/* 필터 바 */}
            <WbsFilterBar
                filters={filters}
                options={filterOptions}
                onChange={setFilters}
                onResetFilters={() => {
                    setFilters({
                        companyId: '',
                        statusKinds: [],
                        weeklyKinds: [],
                        startSoon: false,
                        finishSoon: false,
                        unassignedOnly: false,
                        leafOnly: false,
                        hasRiskOnly: false,
                        hasTaskOnly: false,
                    });
                }}
            />

            {/* 리스트 테이블 */}
            <WbsList
                projectId={projectId}
                // mode={mode}
                // canEdit={canEdit && selectedVersion === currentVersion} // 과거 버전 열림 시 입력 막기
                canAssign={canAssign}
                rows={filteredRows}
                rollupRows={rollupRows}
                // onChangeRows={setRows}
                // onSaveRows={handleSaveRows}
                pvRefDate={pvRefDate}
                refFriday={refFriday}
                flags={flags}
                // onRefreshFlags={() => refreshFlags(rows)}
                counts={counts}
                versions={versions}
                currentListVersion={currentVersion}
                selectedVersion={selectedVersion}
                // actorProjectCompanyId={actorProjectCompanyId}
                // compact={compact}
                onOpenLinks={(m, it) => onOpenLinks(m, it)} // { id: string, ... }
                onChangeVersion={setSelectedVersion}
                filterSummary={filterSummary}
                onResetFilters={() => {
                    setFilters({
                        companyId: '',
                        statusKinds: [],
                        weeklyKinds: [],
                        startSoon: false,
                        finishSoon: false,
                        unassignedOnly: false,
                        leafOnly: false,
                        hasRiskOnly: false,
                        hasTaskOnly: false,
                    });
                }}
                // wbsListId={data?.list?.id ?? ''}
                onAssigned={async () => {
                    await refetch?.();
                    await refreshAll?.();
                }}
                weeklyProgressComparison={weeklyProgressComparison}
                approvedMapW1={approvedMapW1}
                approvedMapW2={approvedMapW2}
                // entryClosed={entryClosed}
                // entryAutoApproveAt={entryWindow.autoApproveAt}
                evViewMode={evViewMode}
                // liveAvailable={liveAvailable}
            />

            {/* 링크 모달 */}
            {linksTarget && (
                <WbsLinksModal
                    open={openLinks}
                    onClose={() => {
                        setOpenLinks(false);
                        setLinksTarget(null);
                        refreshCounts();
                    }}
                    projectId={projectId}
                    item={linksTarget} // { id: string; code; name }
                    mode={linksMode}
                    canEdit={canEdit}
                />
            )}

            {/* 승인/반려 대기 모달 */}
            {canApprove && (
                <ApprovalQueueModal
                    open={approvalOpen}
                    onClose={() => {
                        setApprovalOpen(false);
                        refreshFlags(rows); // 승인/반려 후 갱신
                    }}
                    projectId={projectId}
                    friday={refFriday}
                    rows={rows.map((r) => ({ id: r.id, code: r.code, name: r.name }))} // [STR-ID]
                    flags={flags}
                    canApprove={canApprove}
                    approveEnabled={approveEnabled}
                    approvalOpenAt={entryWindow.closeAt}
                    projectTimeZone={projectTz}
                />
            )}
        </div>
    );
}
